import { useEffect, useState } from "react";
import { Users, UserPlus, UserCheck, Package, ClipboardList, FileWarning, Wallet, CalendarDays } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import axios, { API } from "@/lib/api";
import StatCard from "@/components/shared/StatCard";
import LoadingScreen from "@/components/shared/LoadingScreen";

const COLORS = ["#0F6B4F", "#1677B8", "#F59E0B", "#EF4444", "#8B5CF6"];

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/overview`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data) return <LoadingScreen />;
  const c = data.cards;

  return (
    <div data-testid="admin-dashboard-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Ringkasan Dashboard</h1>
        <p className="text-sm text-[#64748B]">Pantau performa dan aktivitas KBIHU Muhammadiyah Surabaya secara keseluruhan.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard testId="stat-total-jamaah" icon={Users} label="Total Jamaah" value={c.total_jamaah} />
        <StatCard testId="stat-calon-jamaah" icon={UserPlus} label="Calon Jamaah" value={c.calon_jamaah} />
        <StatCard testId="stat-jamaah-aktif" icon={UserCheck} label="Jamaah Aktif" value={c.jamaah_aktif} />
        <StatCard testId="stat-program-aktif" icon={Package} label="Program Aktif" value={c.program_aktif} />
        <StatCard testId="stat-pendaftar-baru" icon={ClipboardList} label="Pendaftar Baru" value={c.pendaftar_baru} />
        <StatCard testId="stat-dokumen-pending" icon={FileWarning} label="Dokumen Menunggu" value={c.dokumen_pending} accent="text-amber-500" />
        <StatCard testId="stat-payment-pending" icon={Wallet} label="Pembayaran Menunggu" value={c.payment_pending} accent="text-amber-500" />
        <StatCard testId="stat-manasik-terdekat" icon={CalendarDays} label="Manasik Terdekat" value={data.upcoming_manasik.length} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-[#0F6B4F]/10 bg-white p-5 shadow-[0_2px_10px_rgba(15,107,79,0.05)]" data-testid="chart-registration-trend">
          <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Tren Pendaftaran</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.charts.registration_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#0F6B4F" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-[#0F6B4F]/10 bg-white p-5 shadow-[0_2px_10px_rgba(15,107,79,0.05)]" data-testid="chart-haji-umrah">
          <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Haji vs Umrah</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.charts.haji_vs_umrah} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} label={({ name, value }) => `${name}: ${value}`}>
                {data.charts.haji_vs_umrah.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-[#0F6B4F]/10 bg-white p-5 shadow-[0_2px_10px_rgba(15,107,79,0.05)]" data-testid="chart-payment-status">
          <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Status Pembayaran</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.charts.payment_status} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} label={({ name, value }) => `${name}: ${value}`}>
                {data.charts.payment_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-[#0F6B4F]/10 bg-white p-5 shadow-[0_2px_10px_rgba(15,107,79,0.05)]" data-testid="chart-registration-status">
          <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Status Pendaftaran</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.charts.registration_status} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={80} />
              <Tooltip />
              <Bar dataKey="value" fill="#1677B8" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
